<template>
  <v-hover>
    <v-card
      slot-scope="{ hover }"
      :class="`elevation-${hover ? 12 : 2}`"
      class="mx-auto c-click c-msg-box c-json"
      style="position:relative;"
      :width="`${wh[0]}px`"
      :height="`${wh[1]}px`"
      @click.native="onClick"
    >
      <template v-if="!files || files.length == 0">
        <div class="flex-row" :style="`width: 100%; height: 100%;`">
          <template v-if="serverRequested">
            <v-icon color="grey" :size="30">broken_image</v-icon>
          </template>
          <template v-else>
            <v-progress-circular class="c-loading" indeterminate :elevation="3" :size="20" :width="2" color="grey" />
          </template>
        </div>
      </template>

      <template v-else-if="files.length == 1">
        <div class="flex-row" :style="`width:${wh[0]}px; height:${wh[1]}px;`">
          <c-img
            :src="`/img/${size(files[0])}/${files[0].id}`"
            class="c-trn c-img"
            :style="`width: ${wh[d90 ? 1 : 0]}px; height: ${wh[d90 ? 0 : 1]}px; ${calcRotate(files[0])}`"
          />
        </div>
      </template>

      <template v-else>
        <div class="flex-col" style="justify-content: space-between; width: 100%; height: 100%">
          <div
            class="flex-row"
            style="overflow:hidden; justify-content: space-between; width: 100%"
            :style="`height:${thumbSize}px;`"
          >
            <talk-msg-album-img :f="files[0]" :width="thumbSize" :height="thumbSize" />
            <talk-msg-album-img :f="files[1]" :width="thumbSize" :height="thumbSize" />
          </div>
          <template v-if="files.length >= 2">
            <div
              class="flex-row"
              style="overflow:hidden; justify-content: space-between; width: 100%"
              :style="`height:${thumbSize}px;`"
            >
              <template v-if="files.length == 3">
                <talk-msg-album-img :f="files[2]" :width="thumbSize * 2 + borderWidth" :height="thumbSize" />
              </template>
              <template v-if="files.length > 3">
                <talk-msg-album-img :f="files[2]" :width="thumbSize" :height="thumbSize" />
                <talk-msg-album-img :f="files[3]" :width="thumbSize" :height="thumbSize" />
              </template>
            </div>
          </template>
          <template v-if="files.length >= 4">
            <div
              class="flex-row"
              style="overflow:hidden; justify-content: space-between; width: 100%"
              :style="`height:${thumbSize}px;`"
            >
              <template v-if="files.length == 5">
                <talk-msg-album-img :f="files[4]" :width="thumbSize * 2 + borderWidth" :height="thumbSize" />
              </template>
              <template v-if="files.length > 5">
                <talk-msg-album-img :f="files[4]" :width="thumbSize" :height="thumbSize" />
                <talk-msg-album-img :f="files[5]" :width="thumbSize" :height="thumbSize" />
              </template>
            </div>
          </template>
          <template v-if="files.length > 6">
            <div class="c-text">+{{ files.length - 6 }}</div>
          </template>
        </div>
      </template>
    </v-card>
  </v-hover>
</template>

<script>
import CacheUtils from '../../utils/CacheUtils';
import AlbumApi from '../../api/album';
import TalkMsgAlbumImg from './TalkMsgAlbumImg';

const THUMB_SIZE = 120;
const MAX_WH_RATE = 1.6;
const BORDER_WIDTH = 1;

export default {
  components: {
    'talk-msg-album-img': TalkMsgAlbumImg,
  },
  props: {
    tid: { type: Number, required: true },
    /*
      export interface TalkMsgForAlbum {
        t: TALK_MSG_TYPE.ALBUM;
        fids: number[];
      }
    */
    data: { type: Object, required: true },
    maxWidth: { type: Number, required: true },
  },
  data() {
    const w = Math.min(this.maxWidth / 2, 80);
    const thumbSize = Math.min(THUMB_SIZE, this.maxWidth / 2) - BORDER_WIDTH / 2;
    return {
      files: null,
      serverRequested: false,
      thumbSize,
      borderWidth: BORDER_WIDTH,
      wh: this.calcDefaultWh(this.data.fids, thumbSize),
      d90: false,
    };
  },
  created() {
    (async () => {
      let cacheKey = '';
      for (const fid of this.data.fids) cacheKey = `${cacheKey}-${fid}`;

      const tid = this.tid;
      const fids = this.data.fids;

      const files = [];
      for (const fid of fids) {
        const f = await CacheUtils.getFile(fid);
        if (f) files.push(f);
      }
      this.files = files;

      const last = await CacheUtils.getFileTalkMsgRequested(cacheKey);
      if (last) return;

      const serverFiles = await AlbumApi.getFiles('talk', tid, fids);
      for (const f of serverFiles) CacheUtils.setFile(f.id, f);

      CacheUtils.setFileTalkMsgRequested(cacheKey, time());
      this.files = serverFiles;
      this.serverRequested = true;
    })();
  },
  mounted() {},
  watch: {
    files(n, o) {
      this.wh = this.calcAllWh(n);
    },
  },
  computed: {},
  methods: {
    calcDefaultWh(fids, thumbSize) {
      if (!fids || fids.length == 0) {
        const w = Math.min(this.maxWidth / 2, 80);
        return [w, w];
      }
      if (fids.length == 1) {
        const maxW = Math.min(THUMB_SIZE * 2 + BORDER_WIDTH, this.maxWidth);
        const h = maxW * MAX_WH_RATE;
        return [maxW, h];
      }
      if (fids.length == 2) {
        return [thumbSize * 2 + BORDER_WIDTH, thumbSize];
      }
      if (fids.length <= 4) {
        return [thumbSize * 2 + BORDER_WIDTH, thumbSize * 2 + BORDER_WIDTH];
      }
      return [thumbSize * 2 + BORDER_WIDTH, thumbSize * 3 + 2 * BORDER_WIDTH];
    },
    calcAllWh(files) {
      if (!files || files.length == 0) {
        const w = Math.min(this.maxWidth / 2, 80);
        this.d90 = false;
        return [w, w];
      }
      if (files.length == 1) {
        const f = files[0];
        this.d90 = this.isD90(f);
        return this.calcWh(f);
      }
      this.d90 = false;
      if (files.length == 2) {
        return [this.thumbSize * 2 + BORDER_WIDTH, this.thumbSize];
      }
      if (files.length <= 4) {
        return [this.thumbSize * 2 + BORDER_WIDTH, this.thumbSize * 2 + BORDER_WIDTH];
      }
      return [this.thumbSize * 2 + BORDER_WIDTH, this.thumbSize * 3 + 2 * BORDER_WIDTH];
    },
    calcWh(f) {
      let whRate = 1;
      const maxW = Math.min(THUMB_SIZE * 2 + BORDER_WIDTH, this.maxWidth);
      const d90 = Math.abs(f.rotate % 180) == 90;
      for (const k of Object.keys(f.img_wh)) {
        try {
          whRate = f.img_wh[k][d90 ? 1 : 0] / f.img_wh[k][d90 ? 0 : 1];
          break;
        } catch (e) {
          console.warn(`failed to calc wh rate ${f.img_wh[k]}`);
        }
      }

      const adjust = whRate > MAX_WH_RATE ? MAX_WH_RATE / whRate : 1;
      return [whRate > 1 ? maxW / adjust : maxW * whRate, whRate > 1 ? maxW / whRate / adjust : maxW];
    },
    calcRotate: function(f) {
      return `transform:rotate(${f.rotate}deg)`;
    },
    isD90(f) {
      return Math.abs(f.rotate % 180) == 90;
    },
    calcRotate(f) {
      return `transform:rotate(${f.rotate}deg)`;
    },
    size(f) {
      const wh = this.wh;
      const w = wh[this.d90 ? 1 : 0] * this.devicePixelRatio;
      let upperMin = null;
      let lowerMax = null;
      for (const k of Object.keys(f.img_wh)) {
        const v = f.img_wh[k];
        if (w <= v[0]) {
          if (!upperMin || v[0] < f.img_wh[upperMin][0]) upperMin = k;
        } else {
          if (!lowerMax || v[0] > f.img_wh[lowerMax][0]) lowerMax = k;
        }
      }
      return upperMin || lowerMax || 'l';
    },
    onClick() {
      if (!this.files || this.files.length == 0) return;
      this.$store.commit('OPEN_VIEWER', { item: this.files[0], items: this.files });
    },
  },
};
</script>

<style scoped>
.c-click {
  cursor: pointer;
  cursor: hand;
}
.c-text {
  position: absolute;
  right: 12px;
  bottom: 12px;
  color: white;
  font-size: 18px;
  text-shadow: #000 1px 1px 2px;
  font-weight: 600;
}
</style>
